import { React, useEffect, useState, useMemo, useRef } from "react";
import SettingsIcon from "@mui/icons-material/Settings";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FilterListIcon from "@mui/icons-material/FilterList";
import { LoaderScreen } from "../../components/LoaderScreen";
import request from "../../../Helpers/API";
import Swal from "sweetalert2";
import { FormatMoneyInt } from "../../../Helpers/FormatNumber";
import {
    MenuItem, FormControl, InputLabel, Select,
    Checkbox, ListItemText,
} from "@mui/material";
import { CapturaEstimacionModal } from "./CapturaEstimacionModal";

const MESES = [
    { value: 1, label: "Enero" },
    { value: 2, label: "Febrero" },
    { value: 3, label: "Marzo" },
    { value: 4, label: "Abril" },
    { value: 5, label: "Mayo" },
    { value: 6, label: "Junio" },
    { value: 7, label: "Julio" },
    { value: 8, label: "Agosto" },
    { value: 9, label: "Septiembre" },
    { value: 10, label: "Octubre" },
    { value: 11, label: "Noviembre" },
    { value: 12, label: "Diciembre" },
];

const KEYWORDS_SINALOA = ["navolato", "caimanero", "costa rica", "eldorado", "la cruz", "villa uni", "el rosario"];
const KEYWORDS_NAYARIT = ["tepic", "tecuala", "santiago", "san vicente", "san jos"];

const getRegionTipo = (nombre) => {
    const n = (nombre || "").toLowerCase();
    if (n.includes("sinaloa")) return "sinaloa";
    if (n.includes("nayarit")) return "nayarit";
    return null;
};

const getKeywordsPorTipo = (tipo) => {
    if (tipo === "sinaloa") return KEYWORDS_SINALOA;
    if (tipo === "nayarit") return KEYWORDS_NAYARIT;
    return [];
};

const getSucursalesDeRegion = (idadr, adrList, sucursalesList) => {
    const region = adrList?.find((a) => a.idadr === idadr);
    if (!region) return [];
    const tipo = getRegionTipo(region.adr);
    const keywords = getKeywordsPorTipo(tipo);
    if (keywords.length === 0) return sucursalesList ?? [];
    return (sucursalesList ?? []).filter((s) =>
        keywords.some((k) => s.descripcion.toLowerCase().includes(k))
    );
};

export const EstimacionVentasScreen = () => {
    // Año y periodo calculados automáticamente de la fecha del sistema
    const hoy = new Date();
    const anio = hoy.getFullYear();
    const periodo = hoy.getMonth() + 1;

    const [openModal, setOpenModal] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [bylineas, setBylineas] = useState(true);
    const [regionesSelected, setRegionesSelected] = useState([]);
    const [sucursalesSelected, setSucursalesSelected] = useState([]);
    const [subtitulo, setSubtitulo] = useState("RESUMEN EJECUTIVO GRUPO");
    const [datos, setDatos] = useState(null);
    const [loader, setLoader] = useState({
        mostrar: false, alerta: false, error: false, mensaje: "",
    });

    const filtersRef = useRef(null);

    const handleOpenModal = () => setOpenModal(true);
    const handleCloseModal = () => setOpenModal(false);

    // Carga inicial al montar
    useEffect(() => {
        GetDatos([], [], true);
    }, []);

    // Cierra el popover de filtros al hacer clic fuera (respeta portales MUI)
    useEffect(() => {
        if (!showFilters) return;
        const handleClickOutside = (e) => {
            if (e.target.closest(".MuiPopover-root, .MuiMenu-root, [data-popper-placement]")) return;
            if (filtersRef.current && !filtersRef.current.contains(e.target)) {
                setShowFilters(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [showFilters]);

    // Sucursales filtradas según regiones activas
    const sucursalesDisponibles = useMemo(() => {
        if (!datos?.sucursales) return [];
        if (regionesSelected.length === 0) return datos.sucursales;
        const result = new Map();
        regionesSelected.forEach((idadr) => {
            getSucursalesDeRegion(idadr, datos.adr, datos.sucursales).forEach((s) =>
                result.set(s.idsucursal, s)
            );
        });
        return Array.from(result.values());
    }, [regionesSelected, datos]);

    // Limpia sucursales inválidas al cambiar región
    useEffect(() => {
        if (!datos) return;
        const validIds = new Set(sucursalesDisponibles.map((s) => s.idsucursal));
        const cleaned = sucursalesSelected.filter((id) => validIds.has(id));
        if (cleaned.length !== sucursalesSelected.length) setSucursalesSelected(cleaned);
    }, [sucursalesDisponibles]);

    const buildIdSucursal = (regsSelected, sucsSelected) => {
        if (!datos?.adr) return "0";
        const totalRegiones = datos.adr.length;
        const todasRegiones = regsSelected.length === 0 || regsSelected.length >= totalRegiones;
        const haySucursales = sucsSelected.length > 0;
        if (todasRegiones && !haySucursales) return "0";
        if (haySucursales) return sucsSelected.join(",");
        const ids = new Map();
        regsSelected.forEach((idadr) => {
            getSucursalesDeRegion(idadr, datos.adr, datos.sucursales).forEach((s) =>
                ids.set(s.idsucursal, true)
            );
        });
        return Array.from(ids.keys()).join(",") || "0";
    };

    const GetDatos = async (regsSelected = regionesSelected, sucsSelected = sucursalesSelected, bylineasParam = bylineas) => {
        setLoader((prev) => ({ ...prev, mostrar: true }));
        try {
            const idsucursal = buildIdSucursal(regsSelected, sucsSelected);
            const result = await request.get(
                `EstimacionVentas/ObtenerVentasEstimadas?bylineas=${bylineasParam}&anio=${anio}&periodo=${periodo}&sucursal=${idsucursal}`
            );
            setDatos(result);
            setLoader((prev) => ({ ...prev, mostrar: false }));
        } catch {
            setLoader((prev) => ({ ...prev, mostrar: false }));
            Swal.fire("Error", "Ocurrió un error al cargar la información.", "error");
        }
    };

    const calcularSubtitulo = (regsSelected, sucsSelected) => {
        if (sucsSelected.length === 1) {
            const suc = datos?.sucursales?.find((s) => s.idsucursal === sucsSelected[0]);
            return `RESUMEN EJECUTIVO ${(suc?.descripcion || "").toUpperCase()}`;
        }
        if (sucsSelected.length > 1) {
            const nombres = sucsSelected
                .map((id) => datos?.sucursales?.find((s) => s.idsucursal === id)?.descripcion || "")
                .filter(Boolean)
                .join(", ");
            return `RESUMEN EJECUTIVO DE ${nombres.toUpperCase()}`;
        }
        if (regsSelected.length === 1) {
            const reg = datos?.adr?.find((a) => a.idadr === regsSelected[0]);
            return `RESUMEN EJECUTIVO ${(reg?.adr || "").toUpperCase()}`;
        }
        return "RESUMEN EJECUTIVO GRUPO";
    };

    const handleBuscar = () => {
        setShowFilters(false);
        setSubtitulo(calcularSubtitulo(regionesSelected, sucursalesSelected));
        GetDatos(regionesSelected, sucursalesSelected, bylineas);
    };

    const handleRegionChange = (e) => {
        const value = e.target.value;
        setRegionesSelected(typeof value === "string" ? value.split(",") : value);
    };

    const handleSucursalChange = (e) => {
        const value = e.target.value;
        setSucursalesSelected(typeof value === "string" ? value.split(",") : value);
    };

    const puedeCapturar = datos?.captura?.captura === 1;

    const calcularAvance = (ventaestimada, objetivo) => {
        if (!objetivo || Number(objetivo) === 0) return 0;
        return (Number(ventaestimada) / Number(objetivo)) * 100;
    };

    // Umbrales: ≥100 verde, >80 amarillo, ≤80 rojo
    const getAvanceClass = (pct) => {
        if (pct >= 100) return "ok";
        if (pct > 80) return "warning";
        return "danger";
    };

    const totales = useMemo(() => {
        if (!datos?.resumen?.length) return null;
        const totalVenta = datos.resumen.reduce((s, r) => s + Number(r.ventaestimada || 0), 0);
        const totalObjetivo = datos.resumen.reduce((s, r) => s + Number(r.objetivo || 0), 0);
        const avance = totalObjetivo > 0 ? (totalVenta / totalObjetivo) * 100 : 0;
        return { totalVenta, totalObjetivo, avance };
    }, [datos]);

    const nombrePeriodo = MESES.find((m) => m.value === periodo)?.label || "";

    const labelRegion =
        regionesSelected.length === 0
            ? "Todas"
            : regionesSelected.length <= 2
            ? regionesSelected
                  .map((id) => datos?.adr?.find((a) => a.idadr === id)?.adr || id)
                  .join(", ")
            : `${regionesSelected.length} regiones`;

    const labelSucursal =
        sucursalesSelected.length === 0
            ? "Todas"
            : `${sucursalesSelected.length} sucursales`;

    return (
        <div className="estimacion-ventas-main-screen">
            <LoaderScreen loader={loader} />

            <div className="ev-body">
                <div className="ev-page">
                    <div className="ev-module">

                        {/* ── Header del módulo ─────────────────────────────────── */}
                        <div className="ev-module-head">
                            <div className="ev-head-grid">

                                {/* Izquierda: Regresar + Filtros */}
                                <div className="ev-head-left">
                                    <button
                                        className="ev-btn-secondary ev-btn-amarillo"
                                        onClick={() => window.history.back()}
                                    >
                                        <ArrowBackIcon fontSize="small" /> Regresar
                                    </button>

                                    <div className="ev-filtros-wrapper" ref={filtersRef}>
                                        <button
                                            className="ev-btn-secondary ev-btn-verde"
                                            onClick={() => setShowFilters((v) => !v)}
                                        >
                                            <FilterListIcon fontSize="small" /> Filtros
                                        </button>

                                        {showFilters && (
                                            <div className="ev-filtros-popover">
                                                <div className="ev-filtros-popover-header">
                                                    <span className="ev-filtros-popover-title">Filtros</span>
                                                    <button
                                                        type="button"
                                                        className="ev-filtros-close"
                                                        onClick={() => setShowFilters(false)}
                                                    >
                                                        ✕
                                                    </button>
                                                </div>

                                                <div className="ev-filtros-options">
                                                    <button
                                                        type="button"
                                                        className={`ev-filtros-toggle${bylineas ? " active" : ""}`}
                                                        onClick={() => setBylineas((v) => !v)}
                                                    >
                                                        <span className="ev-toggle-dot" />
                                                        Ver por líneas
                                                    </button>

                                                    {datos?.adr && (
                                                        <FormControl size="small" fullWidth>
                                                            <InputLabel id="ev-region-label">Región</InputLabel>
                                                            <Select
                                                                labelId="ev-region-label"
                                                                label="Región"
                                                                multiple
                                                                value={regionesSelected}
                                                                onChange={handleRegionChange}
                                                                renderValue={() => labelRegion}
                                                            >
                                                                {datos.adr.map((a) => (
                                                                    <MenuItem key={a.idadr} value={a.idadr}>
                                                                        <Checkbox
                                                                            size="small"
                                                                            checked={regionesSelected.includes(a.idadr)}
                                                                            sx={{ py: 0.5 }}
                                                                        />
                                                                        <ListItemText
                                                                            primary={a.adr}
                                                                            primaryTypographyProps={{ fontSize: 13 }}
                                                                        />
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    )}

                                                    {datos?.sucursales && (
                                                        <FormControl size="small" fullWidth>
                                                            <InputLabel id="ev-sucursal-label">Sucursal</InputLabel>
                                                            <Select
                                                                labelId="ev-sucursal-label"
                                                                label="Sucursal"
                                                                multiple
                                                                value={sucursalesSelected}
                                                                onChange={handleSucursalChange}
                                                                renderValue={() => labelSucursal}
                                                            >
                                                                {sucursalesDisponibles.map((s) => (
                                                                    <MenuItem key={s.idsucursal} value={s.idsucursal}>
                                                                        <Checkbox
                                                                            size="small"
                                                                            checked={sucursalesSelected.includes(s.idsucursal)}
                                                                            sx={{ py: 0.5 }}
                                                                        />
                                                                        <ListItemText
                                                                            primary={s.descripcion}
                                                                            primaryTypographyProps={{ fontSize: 13 }}
                                                                        />
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    )}
                                                </div>

                                                <div className="ev-filtros-footer">
                                                    <button
                                                        type="button"
                                                        className="ev-btn-limpiar"
                                                        onClick={() => {
                                                            setRegionesSelected([]);
                                                            setSucursalesSelected([]);
                                                        }}
                                                    >
                                                        Limpiar
                                                    </button>
                                                    <button className="ev-btn-buscar" onClick={handleBuscar}>
                                                        <SearchIcon fontSize="small" /> Buscar
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Centro: Título + subtítulo */}
                                <div className="ev-head-center">
                                    <h1 className="ev-title">ESTIMACIÓN DE CIERRE DE VENTAS · {nombrePeriodo.toUpperCase()} {anio}</h1>
                                    <p className="ev-head-period">{subtitulo}</p>
                                </div>

                                {/* Derecha: Configurar */}
                                <div className="ev-head-right">
                                    {puedeCapturar && (
                                        <button className="ev-btn-secondary" onClick={handleOpenModal}>
                                            <SettingsIcon fontSize="small" /> Configurar
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Divisor verde con punto amarillo */}
                            <div className="ev-head-divider">
                                <div className="ev-head-divider-dot" />
                            </div>

                        </div>

                        {datos?.resumen && (
                            <div className="ev-tabla-section">
                                {/* Encabezado sticky */}
                                <div className="ev-tabla-head">
                                    <div>Línea</div>
                                    <div>Venta estimada</div>
                                    <div>Objetivo</div>
                                    <div>Avance</div>
                                </div>

                                {/* Filas de datos */}
                                {datos.resumen.map((r, index) => {
                                    const avance = calcularAvance(r.ventaestimada, r.objetivo);
                                    const cls = getAvanceClass(avance);
                                    return (
                                        <div key={index} className="ev-tabla-row">
                                            <div className="ev-cell-linea">{r.linea}</div>
                                            <div className="ev-cell-importe">{FormatMoneyInt(r.ventaestimada)}</div>
                                            <div className="ev-cell-importe">{FormatMoneyInt(r.objetivo)}</div>
                                            <div className="ev-cell-avance">
                                                <div className={`ev-avance-pill ev-avance-pill-${cls}`}>
                                                    <div
                                                        className="ev-avance-fill"
                                                        style={{ width: `${Math.min(avance, 100)}%` }}
                                                    />
                                                    <span className="ev-avance-text">
                                                        {avance.toFixed(1)}%
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}

                                {/* Fila de totales */}
                                {totales && (
                                    <div className="ev-tabla-totales">
                                        <div className="ev-cell-total-label">TOTAL</div>
                                        <div className="ev-cell-total-importe">{FormatMoneyInt(totales.totalVenta)}</div>
                                        <div className="ev-cell-total-importe">{FormatMoneyInt(totales.totalObjetivo)}</div>
                                        <div className="ev-cell-avance">
                                            <div className={`ev-avance-pill ev-avance-pill-${getAvanceClass(totales.avance)}`}>
                                                <div
                                                    className="ev-avance-fill"
                                                    style={{ width: `${Math.min(totales.avance, 100)}%` }}
                                                />
                                                <span className="ev-avance-text">
                                                    {totales.avance.toFixed(1)}%
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── Estado vacío ──────────────────────────────────────── */}
                        {!loader.mostrar && !datos?.resumen && (
                            <div className="ev-empty-state">
                                <p>No hay información disponible para el periodo seleccionado.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <CapturaEstimacionModal
                open={openModal}
                onClose={handleCloseModal}
                anio={anio}
                periodo={periodo}
                sucursales={datos?.sucursales || []}
                onSaved={() => GetDatos(regionesSelected, sucursalesSelected)}
            />
        </div>
    );
};

export default EstimacionVentasScreen;
